export const QR_DOT_STYLE_OPTIONS = [
  { value: 'square', label: 'Square' },
  { value: 'rounded', label: 'Rounded' },
  { value: 'dots', label: 'Dots' },
  { value: 'classy', label: 'Classy' },
  { value: 'classy-rounded', label: 'Classy Rounded' },
  { value: 'extra-rounded', label: 'Extra Rounded' },
];

export const QR_CORNER_SQUARE_STYLE_OPTIONS = [
  { value: 'square', label: 'Square' },
  { value: 'dot', label: 'Dot' },
  { value: 'extra-rounded', label: 'Extra Rounded' },
];

export const QR_CORNER_DOT_STYLE_OPTIONS = [
  { value: 'square', label: 'Square' },
  { value: 'dot', label: 'Dot' },
];

export const DEFAULT_QR_PRESET = 'road-home-soft';

const PRESETS = {
  classic: {
    label: 'Classic',
    foregroundColor: '#006633',
    backgroundColor: '#ffffff',
    dotStyle: 'square',
    cornerSquareStyle: 'square',
    cornerDotStyle: 'square',
  },
  rounded: {
    label: 'Rounded',
    foregroundColor: '#006633',
    backgroundColor: '#ffffff',
    dotStyle: 'rounded',
    cornerSquareStyle: 'extra-rounded',
    cornerDotStyle: 'dot',
  },
  'modern-dots': {
    label: 'Modern Dots',
    foregroundColor: '#1D6F42',
    backgroundColor: '#ffffff',
    dotStyle: 'dots',
    cornerSquareStyle: 'dot',
    cornerDotStyle: 'dot',
  },
  'road-home-soft': {
    label: 'Road Home Soft',
    foregroundColor: '#006633',
    backgroundColor: '#ffffff',
    dotStyle: 'rounded',
    cornerSquareStyle: 'extra-rounded',
    cornerDotStyle: 'dot',
  },
  'high-contrast-print': {
    label: 'High Contrast Print',
    foregroundColor: '#111111',
    backgroundColor: '#ffffff',
    dotStyle: 'square',
    cornerSquareStyle: 'extra-rounded',
    cornerDotStyle: 'square',
  },
};

export const QR_PRESET_OPTIONS = Object.entries(PRESETS).map(([value, preset]) => ({
  value,
  label: preset.label,
}));

export function getQrPresetConfig(preset = DEFAULT_QR_PRESET) {
  return PRESETS[preset] || PRESETS[DEFAULT_QR_PRESET];
}

export function clampQrLogoScale(value, min = 0.1, max = 0.3) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0.2;
  return Math.min(max, Math.max(min, numeric));
}

export function normalizeHexColor(value, fallback = '#000000') {
  const raw = String(value || '').trim();
  if (/^#[0-9a-f]{6}$/i.test(raw)) return raw;
  if (/^#[0-9a-f]{3}$/i.test(raw)) {
    return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`.toLowerCase();
  }
  return fallback;
}

function getRgbChannels(hex) {
  const normalized = normalizeHexColor(hex);
  return {
    r: parseInt(normalized.slice(1, 3), 16),
    g: parseInt(normalized.slice(3, 5), 16),
    b: parseInt(normalized.slice(5, 7), 16),
  };
}

function getRelativeLuminance(channel) {
  const normalized = channel / 255;
  return normalized <= 0.03928
    ? normalized / 12.92
    : ((normalized + 0.055) / 1.055) ** 2.4;
}

export function getQrContrastRatio(foregroundColor, backgroundColor) {
  const foreground = getRgbChannels(foregroundColor);
  const background = getRgbChannels(backgroundColor);
  const lighter = Math.max(
    0.2126 * getRelativeLuminance(foreground.r) +
      0.7152 * getRelativeLuminance(foreground.g) +
      0.0722 * getRelativeLuminance(foreground.b),
    0.2126 * getRelativeLuminance(background.r) +
      0.7152 * getRelativeLuminance(background.g) +
      0.0722 * getRelativeLuminance(background.b)
  );
  const darker = Math.min(
    0.2126 * getRelativeLuminance(foreground.r) +
      0.7152 * getRelativeLuminance(foreground.g) +
      0.0722 * getRelativeLuminance(foreground.b),
    0.2126 * getRelativeLuminance(background.r) +
      0.7152 * getRelativeLuminance(background.g) +
      0.0722 * getRelativeLuminance(background.b)
  );
  return (lighter + 0.05) / (darker + 0.05);
}

export function isQrContrastLow(foregroundColor, backgroundColor, threshold = 4.5) {
  return getQrContrastRatio(foregroundColor, backgroundColor) < threshold;
}

export function buildStyledQrOptions({
  text,
  size = 300,
  margin = 16,
  foregroundColor = '#006633',
  backgroundColor = '#ffffff',
  transparentBackground = false,
  image,
  logoScale = 0.2,
  dotStyle,
  cornerSquareStyle,
  cornerDotStyle,
}) {
  return {
    width: size,
    height: size,
    type: 'svg',
    data: text,
    image,
    margin,
    qrOptions: {
      errorCorrectionLevel: 'H',
    },
    dotsOptions: {
      color: normalizeHexColor(foregroundColor, '#006633'),
      type: dotStyle || getQrPresetConfig(DEFAULT_QR_PRESET).dotStyle,
    },
    cornersSquareOptions: {
      color: normalizeHexColor(foregroundColor, '#006633'),
      type: cornerSquareStyle || getQrPresetConfig(DEFAULT_QR_PRESET).cornerSquareStyle,
    },
    cornersDotOptions: {
      color: normalizeHexColor(foregroundColor, '#006633'),
      type: cornerDotStyle || getQrPresetConfig(DEFAULT_QR_PRESET).cornerDotStyle,
    },
    backgroundOptions: {
      color: transparentBackground
        ? 'transparent'
        : normalizeHexColor(backgroundColor, '#ffffff'),
    },
    imageOptions: {
      hideBackgroundDots: true,
      imageSize: clampQrLogoScale(logoScale),
      margin: 4,
      crossOrigin: 'anonymous',
    },
  };
}
